"use client";

import { KeyboardArrowDown } from "@ellaandil/ui";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ReactNode } from "react";
import {
	Button,
	FieldError,
	Header,
	Label,
	ListBox,
	ListBoxItem,
	type ListBoxItemProps,
	ListBoxSection,
	type ListBoxSectionProps,
	Popover,
	Select as RACSelect,
	type SelectProps as RACSelectProps,
	SelectValue,
	Text,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { useTranslations } from "@/i18n/client";

const fieldStyles = cva(
	[
		"flex items-center w-full outline-none cursor-pointer",
		"border border-solid",
		"rounded-radius-component-specific-input",
		"transition-colors duration-200",
		"bg-input-default-bg",
		"data-[disabled]:bg-input-disabled-bg data-[disabled]:cursor-not-allowed",
	],
	{
		variants: {
			size: {
				medium: "h-10 pl-xs pr-3-xs py-3-xs gap-2-xs",
				large: "h-12 pl-xs pr-2-xs py-2-xs gap-2-xs",
			},
			tone: {
				default: [
					"border-input-default-border",
					"data-[hovered]:border-input-hover-border",
					"data-[focus-visible]:border-input-active-border",
					"data-[open]:border-input-active-border",
					"data-[disabled]:border-input-disabled-border",
				],
				error: "border-input-error-border",
				success: "border-input-success-border",
			},
		},
		defaultVariants: {
			size: "medium",
			tone: "default",
		},
	},
);

const valueStyles = cva(
	[
		"flex-1 min-w-0 truncate text-left",
		"font-body font-regular text-base leading-[1.15]",
		"data-[placeholder]:text-text-dark-text-2",
	],
	{
		variants: {
			textTone: {
				default: "text-text-dark-text-1",
				error: "text-status-error-text data-[placeholder]:text-status-error-text",
				success: "text-text-dark-text-1",
			},
			disabled: {
				true: "text-text-dark-text-2",
				false: "",
			},
		},
		defaultVariants: {
			textTone: "default",
			disabled: false,
		},
	},
);

const messageStyles = cva("font-body text-xs leading-[1.5]", {
	variants: {
		tone: {
			default: "text-text-dark-text-1",
			error: "text-status-error-text",
			success: "text-status-success-text",
		},
	},
	defaultVariants: { tone: "default" },
});

type FieldVariants = VariantProps<typeof fieldStyles>;

export type SelectProps<T extends object> = Omit<RACSelectProps<T>, "children"> & {
	size?: NonNullable<FieldVariants["size"]>;
	label?: ReactNode;
	isOptional?: boolean;
	description?: ReactNode;
	errorMessage?: ReactNode;
	successMessage?: ReactNode;
	isSuccess?: boolean;
	placeholder?: string;
	leadingIcon?: ReactNode;
	children?: ReactNode;
	className?: string;
	fieldClassName?: string;
	popoverClassName?: string;
	listBoxClassName?: string;
};

function SelectInner<T extends object>(
	{
		size = "medium",
		label,
		isOptional = false,
		description,
		errorMessage,
		successMessage,
		isInvalid,
		isSuccess = false,
		isDisabled,
		placeholder,
		leadingIcon,
		children,
		className,
		fieldClassName,
		popoverClassName,
		listBoxClassName,
		...selectProps
	}: SelectProps<T>,
	ref: React.ForwardedRef<HTMLButtonElement>,
) {
	const t = useTranslations("form");
	const tone: "default" | "error" | "success" = isInvalid
		? "error"
		: isSuccess
			? "success"
			: "default";

	return (
		<RACSelect
			{...selectProps}
			isInvalid={isInvalid}
			isDisabled={isDisabled}
			placeholder={placeholder}
			className={twMerge("group/select flex flex-col gap-2-xs w-full", className)}
		>
			{label != null && (
				<div className="flex gap-3-xs items-start w-full">
					<Label className="flex-1 font-body font-regular text-xs uppercase leading-[1.15] text-text-dark-text-1 tabular-nums lining-nums">
						{label}
					</Label>
					{isOptional && (
						<span className="font-body font-regular text-xs uppercase leading-[1.15] text-text-dark-text-2 tabular-nums lining-nums">
							{t("optional")}
						</span>
					)}
				</div>
			)}

			<Button ref={ref} className={twMerge(fieldStyles({ size, tone }), fieldClassName)}>
				{leadingIcon != null && <span className="shrink-0 flex items-center">{leadingIcon}</span>}
				<SelectValue
					className={valueStyles({
						textTone: tone,
						disabled: isDisabled ?? false,
					})}
				>
					{({ isPlaceholder, defaultChildren, selectedText }) =>
						isPlaceholder ? defaultChildren : (selectedText ?? defaultChildren)
					}
				</SelectValue>
				<KeyboardArrowDown
					aria-hidden
					className="shrink-0 size-5 text-text-dark-text-1 transition-transform duration-200 group-data-[open]/select:rotate-180 group-data-[disabled]/select:text-text-dark-text-2"
				/>
			</Button>

			{tone === "error" && errorMessage != null ? (
				<FieldError className={messageStyles({ tone: "error" })}>{errorMessage}</FieldError>
			) : tone === "success" && successMessage != null ? (
				<Text slot="description" className={messageStyles({ tone: "success" })}>
					{successMessage}
				</Text>
			) : description != null ? (
				<Text slot="description" className={messageStyles({ tone: "default" })}>
					{description}
				</Text>
			) : null}

			<Popover
				className={twMerge(
					[
						"w-(--trigger-width)",
						"bg-input-default-bg",
						"border border-solid border-input-default-border",
						"rounded-radius-component-specific-input",
						"shadow-lg",
						"p-3-xs",
						"outline-none",
						"data-[entering]:animate-in data-[entering]:fade-in-0",
						"data-[exiting]:animate-out data-[exiting]:fade-out-0",
					].join(" "),
					popoverClassName,
				)}
			>
				<ListBox
					className={twMerge(
						"flex flex-col gap-3-xs outline-none max-h-72 overflow-auto",
						listBoxClassName,
					)}
				>
					{children}
				</ListBox>
			</Popover>
		</RACSelect>
	);
}

export const Select = forwardRef(SelectInner) as <T extends object>(
	props: SelectProps<T> & { ref?: React.ForwardedRef<HTMLButtonElement> },
) => ReturnType<typeof SelectInner>;

const itemStyles = cva(
	[
		"flex items-center gap-2-xs",
		"h-10 px-2-xs py-2-xs",
		"rounded-radius-component-specific-input",
		"font-body font-regular text-base leading-[1.15] text-text-dark-text-1",
		"cursor-pointer outline-none",
		"truncate",
		"data-[hovered]:bg-neutral-white-90",
		"data-[focused]:bg-neutral-white-90",
		"data-[selected]:bg-neutral-white-90",
		"data-[disabled]:text-text-dark-text-2 data-[disabled]:cursor-not-allowed data-[disabled]:bg-transparent",
	],
	{
		variants: {},
		defaultVariants: {},
	},
);

export type SelectItemProps = ListBoxItemProps & {
	leadingIcon?: ReactNode;
	className?: string;
};

export const SelectItem = forwardRef<HTMLDivElement, SelectItemProps>(
	({ leadingIcon, className, children, ...props }, ref) => {
		return (
			<ListBoxItem ref={ref} {...props} className={twMerge(itemStyles(), className)}>
				{(renderProps) => (
					<>
						{leadingIcon != null && (
							<span className="shrink-0 flex items-center">{leadingIcon}</span>
						)}
						<span className="flex-1 min-w-0 truncate">
							{typeof children === "function" ? children(renderProps) : children}
						</span>
					</>
				)}
			</ListBoxItem>
		);
	},
);

SelectItem.displayName = "SelectItem";

export type SelectSectionProps<T extends object> = Omit<ListBoxSectionProps<T>, "children"> & {
	title?: ReactNode;
	className?: string;
	children?: ReactNode;
};

export function SelectSection<T extends object>({
	title,
	className,
	children,
	...props
}: SelectSectionProps<T>) {
	const sectionChildren: ReactNode = (
		<>
			{title != null && (
				<Header className="px-2-xs pt-2-xs pb-3-xs font-body font-regular text-xs uppercase leading-[1.15] text-text-dark-text-2 tabular-nums lining-nums">
					{title}
				</Header>
			)}
			{children}
		</>
	);
	return (
		<ListBoxSection
			{...(props as ListBoxSectionProps<T>)}
			className={twMerge("flex flex-col gap-3-xs", className)}
		>
			{sectionChildren}
		</ListBoxSection>
	);
}
